import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Session } from "@supabase/supabase-js";

export interface AdminUser {
  id: string;
  email: string;
  full_name: string | null;
  role: "super_admin" | "editor";
  is_active: boolean;
}

export interface AdminSessionState {
  loading: boolean;
  session: Session | null;
  admin: AdminUser | null;
  error: string | null;
}

const INACTIVITY_MS = 24 * 60 * 60 * 1000; // 24h
const ACTIVITY_KEY = "sg_admin_last_activity";

const fetchAdmin = async (email: string): Promise<AdminUser | null> => {
  const { data, error } = await supabase
    .from("admin_users")
    .select("id,email,full_name,role,is_active")
    .ilike("email", email)
    .maybeSingle();
  if (error || !data) return null;
  if (!data.is_active) return null;
  return data as AdminUser;
};

export const useAdminSession = (): AdminSessionState => {
  const [state, setState] = useState<AdminSessionState>({
    loading: true,
    session: null,
    admin: null,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    const hydrate = async (session: Session | null) => {
      if (!session?.user?.email) {
        if (!cancelled) setState({ loading: false, session: null, admin: null, error: null });
        return;
      }
      const { data: verified, error: userError } = await supabase.auth.getUser();
      if (userError || !verified.user?.email || verified.user.id !== session.user.id) {
        if (!cancelled) setState({ loading: false, session: null, admin: null, error: "Your session has expired. Please sign in again." });
        return;
      }
      const admin = await fetchAdmin(verified.user.email.toLowerCase());
      if (cancelled) return;
      if (!admin) {
        setState({
          loading: false,
          session,
          admin: null,
          error: "Your email is not authorized for the admin panel.",
        });
      } else {
        setState({ loading: false, session, admin, error: null });
      }
    };

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      hydrate(session);
    });
    supabase.auth.getSession().then(({ data }) => hydrate(data.session));

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Inactivity tracking
  useEffect(() => {
    if (!state.session) return;
    const bump = () => {
      try {
        localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
      } catch {
        // ignore
      }
    };
    bump();
    const check = () => {
      const last = Number(localStorage.getItem(ACTIVITY_KEY) || 0);
      if (last && Date.now() - last > INACTIVITY_MS) {
        supabase.auth.signOut();
      }
    };
    const interval = window.setInterval(check, 60_000);
    const events = ["click", "keydown", "mousemove", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    return () => {
      window.clearInterval(interval);
      events.forEach((e) => window.removeEventListener(e, bump));
    };
  }, [state.session]);

  return state;
};

export const signOutAdmin = async () => {
  try {
    localStorage.removeItem(ACTIVITY_KEY);
  } catch {
    // ignore
  }
  await supabase.auth.signOut();
};
